/**
 * Отклонения от плана выпуска — то, что диспетчер должен увидеть сам, без звонков.
 *
 * ТЗ (раздел 5) требует подсвечивать: борт не вышел вовремя, не пройден осмотр к плановому
 * времени выезда, смена не вернулась по графику. Плановое время в смене хранится давно, но
 * ни с чем не сравнивалось — и отклонение замечали только когда кто-то звонил.
 *
 * Считается по уже загруженным данным смены, без запросов: доска и так перечитывается раз в
 * полминуты, и лишний вызов ради арифметики был бы напрасным.
 */

export const DEVIATION_KINDS = [
  'DEPARTURE_LATE',
  'EXAMS_LATE',
  'ASSIGNMENT_SILENT',
  'RETURN_LATE',
  'INTEGRATION_ERROR',
] as const;
export type DeviationKind = (typeof DEVIATION_KINDS)[number];

export type DeviationSeverity = 'warn' | 'alert';

export interface Deviation {
  kind: DeviationKind;
  /** Готовая фраза для экрана: диспетчеру нужен смысл, а не код. */
  label: string;
  severity: DeviationSeverity;
  /** На сколько минут просрочено; `null` — просрочка не измеряется. */
  lateMinutes: number | null;
}

/** Пороги в минутах. Вынесены, чтобы правились в одном месте, когда парк назовёт свои. */
export const DEVIATION_THRESHOLDS = {
  /** Насколько позже плана допустим выезд, прежде чем это отклонение. */
  departureGrace: 15,
  /** За сколько до выезда осмотры уже должны быть пройдены. */
  examsBefore: 30,
  /** За сколько до выезда наряд уже должен быть подтверждён водителем. */
  assignmentBefore: 60,
  /** Насколько позже плана допустим заезд. */
  returnGrace: 30,
} as const;

/** Смена в том виде, в каком её знает список: без этого набора отклонения не посчитать. */
export interface DeviationInput {
  status: string;
  plannedDepartureAt: string | Date | null;
  plannedArrivalAt: string | Date | null;
  departureAt: string | Date | null;
  returnAt: string | Date | null;
  driverAssignmentConfirmed: boolean;
  readiness: { medicalPreTripPassed: boolean; technicalPreTripPassed: boolean };
  integration: { esutd: string | null };
}

const FINAL_STATUSES: readonly string[] = ['CLOSED', 'REJECTED'];
const MS_PER_MINUTE = 60_000;

function minutesBetween(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / MS_PER_MINUTE);
}

function asDate(value: string | Date | null): Date | null {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Отклонения смены на момент `now`.
 *
 * Закрытая и отменённая смена молчит: отклонения — про то, во что ещё можно вмешаться.
 * Смена без планового времени тоже молчит по срокам: сравнивать не с чем, и выдумывать
 * плановое время из графика выхода мы не будем — это разные вещи.
 */
export function shiftDeviations(shift: DeviationInput, now: Date): Deviation[] {
  if (FINAL_STATUSES.includes(shift.status)) {
    return [];
  }

  const deviations: Deviation[] = [];
  const plannedDeparture = asDate(shift.plannedDepartureAt);
  const departed = asDate(shift.departureAt) !== null;

  if (plannedDeparture && !departed) {
    const late = minutesBetween(plannedDeparture, now);

    if (late > DEVIATION_THRESHOLDS.departureGrace) {
      deviations.push({
        kind: 'DEPARTURE_LATE',
        label: `Не вышел на линию: опоздание ${late} мин от плана`,
        severity: 'alert',
        lateMinutes: late,
      });
    }

    const beforeDeparture = -late;
    if (beforeDeparture <= DEVIATION_THRESHOLDS.examsBefore) {
      const missing = [
        shift.readiness.medicalPreTripPassed ? null : 'медосмотр',
        shift.readiness.technicalPreTripPassed ? null : 'техосмотр',
      ].filter((item): item is string => item !== null);
      if (missing.length > 0) {
        /**
         * Когда плановое время уже прошло, «до выезда 0 мин» звучит так, будто выезд вот-вот:
         * на доске это стояло рядом с «опоздание 377 мин» и противоречило ему.
         */
        const when =
          beforeDeparture < 0
            ? 'Плановое время выезда прошло'
            : `До выезда ${beforeDeparture} мин`;
        deviations.push({
          kind: 'EXAMS_LATE',
          label: `${when}, не пройден ${missing.join(' и ')}`,
          severity: beforeDeparture < 0 ? 'alert' : 'warn',
          lateMinutes: beforeDeparture < 0 ? -beforeDeparture : null,
        });
      }
    }

    if (
      !shift.driverAssignmentConfirmed &&
      beforeDeparture <= DEVIATION_THRESHOLDS.assignmentBefore
    ) {
      deviations.push({
        kind: 'ASSIGNMENT_SILENT',
        label: 'Водитель не подтвердил наряд',
        severity: beforeDeparture < 0 ? 'alert' : 'warn',
        lateMinutes: beforeDeparture < 0 ? -beforeDeparture : null,
      });
    }
  }

  const plannedArrival = asDate(shift.plannedArrivalAt);
  if (plannedArrival && departed && !asDate(shift.returnAt)) {
    const late = minutesBetween(plannedArrival, now);
    if (late > DEVIATION_THRESHOLDS.returnGrace) {
      deviations.push({
        kind: 'RETURN_LATE',
        label: `Не вернулся в парк: опоздание ${late} мин от плана`,
        severity: 'alert',
        lateMinutes: late,
      });
    }
  }

  if (shift.integration.esutd === 'ERROR') {
    deviations.push({
      kind: 'INTEGRATION_ERROR',
      label: 'Ошибка обмена с ЕСУТД — путевой лист не принят',
      severity: 'alert',
      lateMinutes: null,
    });
  }

  return deviations;
}

/** Худшая степень из набора; `null` — отклонений нет. */
export function worstSeverity(deviations: readonly Deviation[]): DeviationSeverity | null {
  if (deviations.some((item) => item.severity === 'alert')) {
    return 'alert';
  }
  return deviations.length > 0 ? 'warn' : null;
}
